import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { subscriptionsApi } from '../../api/subscriptions';
import { isInTrial } from '../../types/subscription.types';

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 30000;

type ActivationState = 'activating' | 'slow' | 'success';

const CheckoutReturn: React.FC = () => {
  const navigate = useNavigate();
  const [state, setState] = useState<ActivationState>('activating');
  const [planDisplayName, setPlanDisplayName] = useState<string | null>(null);
  const [planPriceDisplay, setPlanPriceDisplay] = useState<string | null>(null);
  const [trialFeeDisplay, setTrialFeeDisplay] = useState<string | null>(null);
  const [trialEnd, setTrialEnd] = useState<string | null>(null);
  const [isTrial, setIsTrial] = useState(false);
  const stoppedRef = useRef(false);

  useEffect(() => {
    const startedAt = Date.now();

    const poll = async () => {
      if (stoppedRef.current) return;

      try {
        const [{ subscription }, { plans }] = await Promise.all([
          subscriptionsApi.getCurrentSubscription(),
          subscriptionsApi.getPlans(),
        ]);
        if (subscription && (isInTrial(subscription) || subscription.status === 'active')) {
          stoppedRef.current = true;
          const matchedPlan = plans.find((p) => p.name === subscription.plan_name);
          setPlanDisplayName(subscription.plan_display_name);
          setPlanPriceDisplay(matchedPlan?.price_display ?? null);
          setTrialFeeDisplay(matchedPlan?.trial_fee_display ?? null);
          setIsTrial(isInTrial(subscription));
          setTrialEnd(
            subscription.current_period_end
              ? new Date(subscription.current_period_end).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })
              : null
          );
          setState('success');
          return;
        }
      } catch (error) {
        console.error('Failed to poll subscription activation status:', error);
      }

      if (Date.now() - startedAt >= POLL_TIMEOUT_MS) {
        stoppedRef.current = true;
        setState('slow');
        return;
      }

      setTimeout(poll, POLL_INTERVAL_MS);
    };

    poll();
    return () => {
      stoppedRef.current = true;
    };
  }, []);

  return (
    <div className="flex min-h-screen items-center justify-center bg-light px-4">
      <div className="w-full max-w-md text-center">
        <div className="mb-8 flex items-center justify-center gap-2">
          <span className="text-xl font-extrabold tracking-tight text-primary">WebLeads</span>
        </div>

        {state === 'activating' && (
          <>
            <h1 className="text-2xl font-extrabold tracking-tight text-secondary sm:text-3xl">
              Activating your trial...
            </h1>
            <p className="mt-3 text-[15px] text-gray-500">This takes a few seconds. Don&apos;t close this tab.</p>
            <div className="relative mx-auto mt-7 h-1.5 max-w-sm overflow-hidden rounded-full bg-gray-200">
              <span className="absolute inset-y-0 w-2/5 animate-pulse rounded-full bg-gradient-to-r from-accent to-map-green" />
            </div>
          </>
        )}

        {state === 'slow' && (
          <>
            <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-accent/10">
              <svg viewBox="0 0 24 24" fill="none" stroke="#4BA1C5" strokeWidth={2} className="h-6 w-6">
                <circle cx="12" cy="12" r="9" />
                <path d="M12 7v5l3 2" />
              </svg>
            </div>
            <h1 className="text-2xl font-extrabold tracking-tight text-secondary sm:text-3xl">
              Taking longer than usual
            </h1>
            <p className="mx-auto mt-3 max-w-sm text-[15px] leading-relaxed text-gray-500">
              We&apos;ll email you the moment your trial is active. You can head to your dashboard in the meantime.
            </p>
            <button
              onClick={() => navigate('/dashboard')}
              className="mt-7 rounded-xl bg-primary px-8 py-3.5 text-sm font-semibold text-white shadow-[0_8px_20px_rgba(138,48,51,0.22)] hover:bg-primary-hover"
            >
              Go to dashboard
            </button>
          </>
        )}

        {state === 'success' && (
          <>
            <div className="mx-auto mb-5 flex h-[60px] w-[60px] items-center justify-center rounded-full bg-map-green shadow-[0_8px_22px_rgba(91,157,127,0.34)]">
              <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={3} className="h-7 w-7">
                <path d="m5 12 5 5 9-11" />
              </svg>
            </div>
            <h1 className="text-2xl font-extrabold tracking-tight text-secondary sm:text-3xl">
              {isTrial ? 'Your 5-day trial is live' : `You're on ${planDisplayName ?? 'your plan'}`}
            </h1>

            <div className="mx-auto mt-7 max-w-sm rounded-2xl border border-gray-200 bg-white px-6 py-5 text-left shadow-[0_8px_28px_rgba(45,49,66,0.08)]">
              <div className="flex items-center justify-between border-b border-gray-100 pb-3.5">
                <span className="text-[17px] font-bold text-secondary">{planDisplayName ?? 'Your'} plan</span>
                <span className="rounded-full bg-accent/10 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-accent">
                  {isTrial ? 'Trial active' : 'Active'}
                </span>
              </div>
              <div className="flex flex-col gap-2.5 pt-3.5 text-sm">
                {isTrial && trialFeeDisplay && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Paid today</span>
                    <span className="font-semibold text-secondary">{trialFeeDisplay}</span>
                  </div>
                )}
                {trialEnd && isTrial && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Trial ends</span>
                    <span className="font-semibold text-secondary">{trialEnd}</span>
                  </div>
                )}
              </div>
            </div>

            {isTrial && planPriceDisplay && trialEnd && (
              <div className="mx-auto mt-4 flex max-w-sm items-center justify-between gap-4 rounded-xl border border-amber-200 bg-amber-50 px-5 py-4 text-left">
                <div>
                  <div className="text-[11.5px] font-bold uppercase tracking-wide text-amber-800">
                    Next charge, full price
                  </div>
                  <div className="mt-1 text-[13px] text-amber-900/80">
                    Charged automatically, not credited from today&apos;s fee
                  </div>
                </div>
                <div className="whitespace-nowrap text-right">
                  <div className="text-xl font-extrabold text-secondary">{planPriceDisplay}</div>
                  <div className="text-[12.5px] font-semibold text-gray-500">on {trialEnd}</div>
                </div>
              </div>
            )}

            <div className="mx-auto mt-4 flex max-w-sm items-start gap-3 rounded-xl border border-accent/30 bg-accent/10 px-4 py-4 text-left">
              <svg viewBox="0 0 24 24" fill="none" stroke="#2E6F87" strokeWidth={2} className="mt-0.5 h-5 w-5 flex-none">
                <circle cx="12" cy="12" r="9" />
                <path d="M12 7v5l3 2" />
              </svg>
              <p className="text-sm leading-relaxed text-[#245A70]">
                <b className="font-bold">Searches take about 30-40 minutes to complete.</b> We&apos;ll email you when
                your results are ready.
              </p>
            </div>

            <button
              onClick={() => navigate('/searches/new')}
              className="mt-7 rounded-xl bg-primary px-9 py-3.5 text-sm font-semibold text-white shadow-[0_10px_24px_rgba(138,48,51,0.24)] hover:bg-primary-hover"
            >
              Run your first search
            </button>
            <div className="mt-4">
              <button onClick={() => navigate('/subscriptions')} className="text-[13.5px] font-semibold text-gray-500 hover:text-primary">
                Manage trial
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CheckoutReturn;
